import type ProductWizardController from "../product_wizard_controller";
import { esc, getSearchableSelectLabel, getCurrentPriceUom } from "./helpers";
import { fetchConversion } from "./conversion_calculator";
// Suite::Forms::MultiImageUpload overrides its stimulus_identifier_path to
// the Daisy path, so the data attributes on its DOM are emitted under the
// Daisy identifier (the JS controller is registered there).
import { DecorDaisyFormsMultiImageUploadIdentifier } from "decor";

/** Populate the full review step HTML. */
export function populateReview(controller: ProductWizardController): void {
  if (!controller.hasReviewContentTarget) return;

  const form = controller.element.querySelector("form");
  if (!form) return;

  const getValue = (name: string): string => {
    const input = form.querySelector(
      `[name*="[${name}]"]`,
    ) as HTMLInputElement | null;
    return input?.value?.trim() || "";
  };

  const reviewField = (label: string, value: string): string => {
    if (!value) return "";
    return `<div class="flex justify-between py-1.5">
      <dt class="text-sm text-gray-500">${esc(label)}</dt>
      <dd class="text-sm font-medium text-gray-900 text-right max-w-[60%]">${esc(value)}</dd>
    </div>`;
  };

  const sectionHeader = (title: string, step: number, icon: string): string => {
    return `<div class="flex items-center justify-between border-b border-gray-200 pb-2 mb-3">
      <h4 class="text-sm font-semibold text-gray-900 flex items-center gap-2">
        <span class="text-base">${icon}</span> ${esc(title)}
      </h4>
      <button type="button" data-action="click->platform-admin--product-wizard#goToStep" data-step="${step}"
              class="text-xs text-primary hover:text-primary-700 font-medium">Edit</button>
    </div>`;
  };

  // Gather values
  const sku = getValue("sku");
  const title = getValue("short_description");
  const mediumDesc = getValue("medium_description");
  const longDesc = getValue("long_description");
  const category = getSearchableSelectLabel(controller, "product_category_id");
  const standardizedProduct = getSearchableSelectLabel(
    controller,
    "standardized_product_id",
  );
  const grade = getSearchableSelectLabel(controller, "product_grade_id");
  const brand = getSearchableSelectLabel(controller, "product_brand_id");
  const newBrandName = getValue("new_brand_name");
  const country = getSearchableSelectLabel(controller, "country_of_origin");
  const freshOrFrozen = getSearchableSelectLabel(controller, "fresh_or_frozen");
  const wildOrFarmed = getSearchableSelectLabel(controller, "wild_or_farmed");
  const species = getValue("primary_source_species");
  const allergenEls = form.querySelectorAll(
    '[name*="[allergens]"] [data-item-id]',
  ) as NodeListOf<HTMLElement>;
  const allergenLabels: string[] = [];
  allergenEls.forEach((el) => {
    const span = el.querySelector("span");
    if (span?.textContent?.trim()) allergenLabels.push(span.textContent.trim());
  });
  // Fallback: read hidden inputs
  if (allergenLabels.length === 0) {
    const allergenInputs = form.querySelectorAll(
      'input[type="hidden"][name*="[allergens]"]',
    ) as NodeListOf<HTMLInputElement>;
    allergenInputs.forEach((el) => {
      if (el.value) allergenLabels.push(el.value);
    });
  }
  const priceUom = getSearchableSelectLabel(controller, "default_price_uom");

  // Gather tags from multi-select
  const tagEls = form.querySelectorAll(
    '[name*="[product_tag_ids][]"]',
  ) as NodeListOf<HTMLInputElement>;
  const tagLabels: string[] = [];
  tagEls.forEach((el) => {
    if (el.value) {
      const chip = el
        .closest("[data-selected-item]")
        ?.querySelector("[data-label]");
      if (chip?.textContent?.trim()) {
        tagLabels.push(chip.textContent.trim());
      }
    }
  });

  // Build sections
  let html = '<div class="space-y-6">';

  // Section 1: Identity
  html += `<div class="bg-white border border-gray-200 rounded-lg p-4">`;
  html += sectionHeader("SKU & Title", 1, "1");
  html += `<dl class="divide-y divide-gray-100">`;
  html += reviewField("SKU", sku);
  html += reviewField("Title", title);
  html += reviewField("Medium Description", mediumDesc);
  html += reviewField("Long Description", longDesc);
  html += `</dl></div>`;

  // Section 2: Category
  html += `<div class="bg-white border border-gray-200 rounded-lg p-4">`;
  html += sectionHeader("Category", 2, "2");
  html += `<dl class="divide-y divide-gray-100">`;
  html += reviewField("Category", category);
  html += `</dl></div>`;

  // Section 3: Details
  const hasDetails =
    standardizedProduct || grade || brand || newBrandName || country;
  if (hasDetails) {
    html += `<div class="bg-white border border-gray-200 rounded-lg p-4">`;
    html += sectionHeader("Details", 3, "3");
    html += `<dl class="divide-y divide-gray-100">`;
    html += reviewField("Standardized Product", standardizedProduct);
    html += reviewField("Grade", grade);
    html += reviewField("Brand", brand || newBrandName);
    html += reviewField("Country of Origin", country);
    html += `</dl></div>`;
  }

  // Section 5: Metadata
  const hasMetadata =
    freshOrFrozen ||
    wildOrFarmed ||
    species ||
    allergenLabels.length > 0 ||
    tagLabels.length > 0;
  if (hasMetadata) {
    html += `<div class="bg-white border border-gray-200 rounded-lg p-4">`;
    html += sectionHeader("Metadata & Tags", 5, "5");
    html += `<dl class="divide-y divide-gray-100">`;
    html += reviewField("Fresh / Frozen", freshOrFrozen);
    html += reviewField("Wild / Farmed", wildOrFarmed);
    html += reviewField("Species", species);
    if (allergenLabels.length > 0) {
      html += reviewField("Allergens", allergenLabels.join(", "));
    }
    if (tagLabels.length > 0) {
      html += reviewField("Tags", tagLabels.join(", "));
    }
    html += `</dl></div>`;
  }

  // Section 4: Images
  const imageUrls = getImageDataUrls(controller);
  if (imageUrls.length > 0) {
    html += `<div class="bg-white border border-gray-200 rounded-lg p-4">`;
    html += sectionHeader("Images", 4, "4");
    html += `<div class="flex gap-2 flex-wrap">`;
    imageUrls.forEach((url, idx) => {
      html += `<img src="${url}" alt="Image ${idx + 1}" class="w-20 h-20 rounded border border-gray-200 object-cover" crossorigin="anonymous">`;
    });
    html += `</div>`;
    html += `<p class="text-xs text-gray-500 mt-2">${imageUrls.length} image${imageUrls.length !== 1 ? "s" : ""} selected</p>`;
    html += `</div>`;
  }

  // Section 6: Pricing
  const priceUomRaw = getCurrentPriceUom(controller);
  const priceDisplay = priceUom || priceUomRaw;
  html += `<div class="bg-white border border-gray-200 rounded-lg p-4">`;
  html += sectionHeader("Pricing", 6, "6");
  html += `<dl class="divide-y divide-gray-100">`;
  if (priceDisplay) {
    html += reviewField("Price Unit of Measure", priceDisplay);
  } else {
    html += `<p class="text-sm text-amber-600 italic py-1">No price UoM selected. <button type="button" data-action="click->platform-admin--product-wizard#goToStep" data-step="6" class="underline">Go to step 6 to set it.</button></p>`;
  }
  html += `</dl></div>`;

  // Section 7: Variants
  html += `<div class="bg-white border border-gray-200 rounded-lg p-4">`;
  html += sectionHeader("Variants", 7, "7");
  html += buildVariantReviewHtml(controller);
  html += `</div>`;

  html += "</div>";

  controller.reviewContentTarget.innerHTML = html;
}

/** Build the HTML for the variant review section. */
export function buildVariantReviewHtml(
  controller: ProductWizardController,
): string {
  const variants = controller.variantsContainerTarget.querySelectorAll(
    "[data-variant-entry]",
  );
  const priceUom = getCurrentPriceUom(controller);

  if (variants.length === 0) {
    return '<p class="text-sm text-amber-600 italic">No variants configured. At least one variant is required.</p>';
  }

  let html = `<div class="space-y-4">`;

  variants.forEach((variant, index) => {
    const getVal = (name: string): string => {
      const el = variant.querySelector(`[name*="[${name}]"]`) as
        | HTMLInputElement
        | HTMLSelectElement
        | null;
      return el?.value?.trim() || "";
    };

    const getSelectText = (name: string): string => {
      const el = variant.querySelector(
        `[name*="[${name}]"]`,
      ) as HTMLSelectElement | null;
      if (el?.selectedOptions?.length) {
        return el.selectedOptions[0].text || el.value || "";
      }
      return el?.value || "";
    };

    const sellUom = getSelectText("sell_uom");
    const sellWeight = getVal("sell_weight");
    const sellWeightUom = getSelectText("sell_weight_uom");
    const sellCount = getVal("sell_count");
    const partCount = getVal("part_count");
    const extraCharge = getVal("extra_charge_per_uom");
    const currencyCode = getVal("currency_code");

    html += `<div class="border border-gray-200 rounded-lg p-4 bg-white">`;
    html += `<div class="text-sm font-semibold text-gray-800 mb-2">Variant ${index + 1}: Sold by ${esc(sellUom || "N/A")}</div>`;

    // Details grid
    const details: string[] = [];
    if (sellWeight && sellWeightUom) {
      details.push(
        `<span class="text-gray-500">Weight:</span> ${esc(sellWeight)} ${esc(sellWeightUom)}`,
      );
    }
    if (sellCount) {
      details.push(
        `<span class="text-gray-500">Count:</span> ${esc(sellCount)} per unit`,
      );
    }
    if (partCount) {
      details.push(
        `<span class="text-gray-500">Pieces:</span> ${esc(partCount)}`,
      );
    }
    if (extraCharge) {
      details.push(
        `<span class="text-gray-500">Extra charge:</span> ${esc(currencyCode || "")} ${esc(extraCharge)}`,
      );
    }

    if (details.length > 0) {
      html += `<div class="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-700 mb-3">${details.join("")}</div>`;
    }

    // Conversion calculator
    if (priceUom) {
      const cardId = `review-variant-${index}`;
      html += `<div class="mt-2 pt-2 border-t border-gray-100">`;
      html += `<div class="flex items-center gap-3">`;
      html += `<label class="text-xs font-medium text-gray-500">Test quantity:</label>`;
      html += `<input type="number" min="1" value="1" step="1"
                 class="w-20 text-sm rounded border-gray-300 px-2 py-1"
                 data-variant-index="${index}"
                 data-action="input->platform-admin--product-wizard#calculateConversion">`;
      html += `<span class="text-sm font-medium text-gray-700" id="${cardId}-result">calculating...</span>`;
      html += `</div></div>`;
    }

    html += `</div>`;
  });

  html += `</div>`;

  // Trigger initial conversion calculations after a tick (DOM needs to render first)
  setTimeout(() => {
    variants.forEach((_v, i) => fetchConversion(controller, i, 1, "review"));
  }, 100);

  return html;
}

/** Update variant preview cards in the side panel. */
export function updateVariantPreviewCards(
  controller: ProductWizardController,
): void {
  const container = controller.previewVariantsListTarget;
  const variants = controller.variantsContainerTarget.querySelectorAll(
    "[data-variant-entry]",
  );

  if (variants.length === 0) {
    container.innerHTML =
      '<p class="text-sm text-gray-400 italic">No variants yet</p>';
    return;
  }

  const priceUom = getCurrentPriceUom(controller);
  const cards: string[] = [];

  variants.forEach((variant, index) => {
    const sellUomSelect = variant.querySelector(
      '[name*="[sell_uom]"]',
    ) as HTMLSelectElement | null;
    const sellUom = sellUomSelect?.value;
    const sellUomLabel =
      sellUomSelect?.selectedOptions[0]?.text || sellUom || "\u2014";

    if (!sellUom) {
      cards.push(
        `<div class="text-sm text-gray-400 italic">Variant ${index + 1}: no sell UoM selected</div>`,
      );
      return;
    }

    // Gather bridge info for display
    const weightInput = variant.querySelector(
      '[name*="[sell_weight]"]',
    ) as HTMLInputElement | null;
    const weightUomSelect = variant.querySelector(
      '[name*="[sell_weight_uom]"]',
    ) as HTMLSelectElement | null;
    const countInput = variant.querySelector(
      '[name*="[sell_count]"]',
    ) as HTMLInputElement | null;
    const partCountInput = variant.querySelector(
      '[name*="[part_count]"]',
    ) as HTMLInputElement | null;

    let bridgeInfo = "";
    if (weightInput?.value && weightUomSelect?.value) {
      bridgeInfo = `${esc(weightInput.value)} ${esc(weightUomSelect.selectedOptions[0]?.text || weightUomSelect.value)}`;
    } else if (countInput?.value) {
      bridgeInfo = `${esc(countInput.value)} per unit`;
    }

    const partInfo = partCountInput?.value
      ? `${esc(partCountInput.value)} pieces`
      : "";

    // Build card with quantity input
    const cardId = `preview-variant-${index}`;
    let cardHtml = `
      <div class="border border-gray-200 rounded-md p-3 bg-gray-50 text-sm" id="${cardId}">
        <div class="font-medium text-gray-700">Sold by: ${esc(sellUomLabel)}</div>
        ${bridgeInfo ? `<div class="text-xs text-gray-500">Weight: ${bridgeInfo}</div>` : ""}
        ${partInfo ? `<div class="text-xs text-gray-500">Contains: ${partInfo}</div>` : ""}
    `;

    if (priceUom && priceUom !== sellUom) {
      cardHtml += `
        <div class="mt-2 flex items-center gap-2">
          <label class="text-xs text-gray-500">Qty:</label>
          <input type="number" min="1" value="1" step="1"
                 class="w-16 text-xs rounded border-gray-300 px-1.5 py-1"
                 data-variant-index="${index}"
                 data-action="input->platform-admin--product-wizard#calculateConversion">
          <span class="text-xs text-gray-500" id="${cardId}-result">= ...</span>
        </div>
      `;
    }

    cardHtml += "</div>";
    cards.push(cardHtml);
  });

  container.innerHTML = cards.join("");

  // Auto-calculate conversions for each variant at qty 1
  variants.forEach((_variant, index) => {
    fetchConversion(controller, index, 1);
  });
}

/** Trigger an image preview update (with slight delay for upload controller). */
export function updateImagePreview(controller: ProductWizardController): void {
  if (
    !controller.hasPreviewImagesTarget ||
    !controller.hasPreviewImagesSectionTarget
  )
    return;

  // Delay slightly so the multi-image-upload controller can update thumbnails first
  setTimeout(() => renderImagePreview(controller), 150);
}

/** Render image thumbnails in the preview panel. */
export function renderImagePreview(controller: ProductWizardController): void {
  if (
    !controller.hasPreviewImagesTarget ||
    !controller.hasPreviewImagesSectionTarget
  )
    return;

  const thumbnails = controller.element.querySelectorAll<HTMLImageElement>(
    `[data-controller*="${DecorDaisyFormsMultiImageUploadIdentifier}"] [data-${DecorDaisyFormsMultiImageUploadIdentifier}-target="thumbnail"] > img`,
  );

  if (thumbnails.length === 0) {
    controller.previewImagesSectionTarget.classList.add("hidden");
    controller.previewImagesTarget.innerHTML = "";
    return;
  }

  controller.previewImagesSectionTarget.classList.remove("hidden");
  const container = controller.previewImagesTarget;
  container.innerHTML = "";

  thumbnails.forEach((img) => {
    const thumb = document.createElement("img");
    thumb.src = img.src;
    thumb.className = "w-12 h-12 rounded border border-gray-200 object-cover";
    container.appendChild(thumb);
  });
}

/** Collect image data URLs from the multi-image-upload thumbnails. */
export function getImageDataUrls(
  controller: ProductWizardController,
): string[] {
  const urls: string[] = [];
  const thumbnails = controller.element.querySelectorAll<HTMLImageElement>(
    `[data-controller*="${DecorDaisyFormsMultiImageUploadIdentifier}"] [data-${DecorDaisyFormsMultiImageUploadIdentifier}-target="thumbnail"] > img`,
  );
  thumbnails.forEach((img) => {
    if (img.src) urls.push(img.src);
  });
  return urls;
}
